<template>
    <div>
        <div class="d-flex justify-space-between align-center">
            <h1 class="text-h5 primary--text">{{$t('PackingLists.packing-lists')}}</h1>
            <!-- delete-dialog is decleared here to use it as ref -->
            <delete-dialog ref="DeleteDialog" @on-delete="filter">
                <span class="text-body-1">
                    {{$t('are-you-sure-you-want-to')}}
                    <span class="text-uppercase error--text">{{$t('delete')}}</span> 
                    {{$t('PackingLists.the-packing-list')}}
                </span>
            </delete-dialog>

            <!-- Add a packing list -->
            <v-btn
              v-if="!viewerOnly"
              text
              small
              class="lightPrimary white--text"
              :to="{
                name:'packing-list',
                params:{companyId: $route.params.companyId},
              }"
            >
                {{$t('PackingLists.add-a-packing-list')}}
            </v-btn>
        </div>

        <!-- filter -->
        <v-form class="my-2" @submit.prevent="filter()">
            <v-row class="align-center ma-0">
                <v-col cols="auto" class="text-body-1 primary--text px-1 pt-0 pb-sm-0 pb-1">
                    {{$t('filter')}}
                </v-col>
                
                <!-- To -->
                <v-col md="3" sm="4" cols="12" class="px-1 pt-0 pb-sm-0 pb-1">
                    <v-combobox
                        v-model="to"
                        :label="$t('PackingLists.to')"
                        :items="customers"
                        item-text="name"
                        item-value="name"
                        :return-object="false"
                        :loading="filterLoading"
                        :disabled="filterLoading"
                        filled
                        outlined
                        hide-details
                        dense
                    ></v-combobox>
                </v-col>

                <!-- date -->
                <v-col lg="2" md="3" sm="4" cols="12" class="px-1 pt-0 pb-sm-0 pb-1">
                    <v-menu
                        ref="menuOfDate"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="date ? moment(date) : null"
                                :label="$t('headers.date')"
                                prepend-icon="mdi-calendar"
                                dense
                                filled
                                outlined
                                hide-details
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                :disabled="loading"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="date"
                            :max="new Date().toISOString().substr(0, 10)"
                            min="1950-01-01"
                            @change="$refs.menuOfDate.save(date)"
                        ></v-date-picker>
                    </v-menu>
                </v-col>

                <v-spacer/>

                <v-col cols="auto" class="px-1 pt-0 pb-sm-0 pb-1">
                    <!-- search button -->
                    <v-btn
                        text
                        color="success"
                        class="px-sm-4"
                        :loading="loading"
                        :disabled="loading || isAllFieldsEmpty"
                        @click="isFiltered = true"
                        type="submit"
                    >
                        {{$t('search')}}
                    </v-btn>
                    <v-btn
                        v-if="isFiltered"
                        icon
                        small
                        class="px-sm-4"
                        @click="fetchPackingLists();"
                        :disabled="!isFiltered || loading"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
        
        <!-- data table -->
        <v-data-table
          :headers="headers"
          :items="loading ? [] : packingLists"
          :items-per-page="20"
          :no-data-text="$t('no-data-text')"
          :no-results-text="$t('no-results-text')"
          :loading-text="$t('loading-text')"
          :footer-props="{
            'items-per-page-options': [10, 20, 40, 100, -1],
            'items-per-page-all-text': $t('items-per-page-all-text'),
            'items-per-page-text': $t('items-per-page-text'),
            showCurrentPage:true
          }"
          :loading="loading"
          disable-sort
          dense
          class="elevation-2"
        >
            <!-- buyer -->
            <template v-slot:item.to="{item}">
                {{item.to ? getBuyerName(item.to) : 'N/A'}}
            </template>

            <!-- reference Contract -->
            <template v-slot:item.referenceContract="{item}">
                {{item.referenceContract ? item.referenceContract : 'N/A'}}
            </template>
            
            <!-- reference Invoice -->
            <template v-slot:item.referenceInvoice="{item}">
                {{item.referenceInvoice ? item.referenceInvoice : 'N/A'}}
            </template>

            <!-- date -->
            <template v-slot:item.date="{item}">
                {{ item.date ? moment(item.date) : 'N/A'}}
            </template>

            <!-- packing date -->
            <template v-slot:item.packingDate="{item}">
                {{ item.packingDate ? findMonthDate(item.packingDate) : 'N/A'}}
            </template>

            <!-- expiry date -->
            <template v-slot:item.expiryDate="{item}">
                {{ item.expiryDate ? findMonthDate(item.expiryDate) : 'N/A'}}
            </template>

            <!-- packing -->
            <template v-slot:item.packing="{item}">
                {{ item.packing ? item.packing : 'N/A' }}
            </template>

            <!-- actions -->
            <template v-slot:item.actions="{item}">
                <div class="d-flex justify-center">
                    <tooltip :text="$t('tooltips.download-as-pdf')">
                        <v-btn text small @click="report(item.id)" target="_blank">
                            <v-icon>
                                mdi-tray-arrow-down
                            </v-icon>
                        </v-btn>
                    </tooltip>

                    <template v-if="viewerOnly">
                        <tooltip :text="$t('tooltips.preview')">
                            <v-btn
                                text
                                small
                                :to="{
                                    name:'packing-list',
                                    params:{companyId: $route.params.companyId},
                                    query:{viewer: 1, id:item.id}
                                }"
                            >
                                <v-icon>
                                    mdi-eye-outline
                                </v-icon>
                            </v-btn>
                        </tooltip>
                    </template>

                    <template v-else>
                        <tooltip :text="$t('tooltips.duplicate')">
                            <v-btn
                                text
                                small
                                :to="{
                                    name:'packing-list',
                                    params:{companyId: $route.params.companyId},
                                    query:{duplicate: 1, id:item.id}
                                }"
                            >
                                <v-icon>
                                    mdi-content-duplicate
                                </v-icon>
                            </v-btn>
                        </tooltip>
                        
                        <tooltip :text="$t('tooltips.edit')">
                            <v-btn
                                text
                                small
                                :to="{
                                    name:'packing-list',
                                    params:{companyId: $route.params.companyId},
                                    query:{id:item.id}
                                }"
                            >
                                <v-icon>
                                    mdi-pencil
                                </v-icon>
                            </v-btn>
                        </tooltip>
                        
                        <tooltip :text="$t('tooltips.delete')">
                            <v-btn @click="$refs.DeleteDialog.activeDialog(item.id, 'packingLists')" text small>
                                <v-icon>
                                    mdi-delete
                                </v-icon>
                            </v-btn>
                        </tooltip>
                    </template>
                </div>
            </template>

            <!-- footer -->
            <template v-slot:footer.page-text="{pageStart, pageStop, itemsLength}">
                {{pageStart}} - {{pageStop}} {{$t('of')}} {{itemsLength}}
            </template>
        </v-data-table>
    </div>
</template>

<script>
import DeleteDialog from '../../components/DeleteDialog'
import Tooltip from '../../components/Tooltip'
import { mapState, mapGetters } from 'vuex'
import { findMonthDate, getBuyerName } from '../../helpers'
import { BASE_API_URL } from '../../constants'

export default {
    data: () => ({
        // filter data
        to: null,
        date: null,

        // helpers
        loading: false,
        filterLoading: false,
        isFiltered: false
    }),

    components:{
        DeleteDialog,
        Tooltip
    },

    methods:{
        // filter funciton
        clearFilter(){
            this.to = null;
            this.date = null;

            this.isFiltered = false;
        },
        
        filter(){
            this.loading = true;
            this.$store.dispatch('packingLists/fetchAll', {
                to: this.to,
                date: this.date
            })
            .finally(() => {
                this.loading = false;
            })
        },

        fetchPackingLists(){
            this.clearFilter();
            this.filter();
        },

        // report
        report(id){
            window.open(BASE_API_URL + `/reports/${this.$route.params.companyId}/packingLists/${id}`);
        },

        // table funcitons
        moment(date){
            return window.moment(date).format(this.fileSettings.dateFormat ? this.fileSettings.dateFormat.toUpperCase() : 'DD-MM-YYYY');
        },

        // helpers
        findMonthDate,
        getBuyerName,
    },

    computed:{
        isAllFieldsEmpty(){
            return this.to === null && this.date === null;
        },

        ...mapState({
            customers: state => state.customers.customers,
            packingLists: state => state.packingLists.packingLists,
            viewerOnly: state => state.users.viewerOnly,
            fileSettings: state => state.fileSettings.fileSettings
        }),

        headers() {
            const headers = [
                { text: this.$t('headers.date'), value:'date', class:'primary--text fixed-width-date', cellClass:'primary--text fixed-width-date', width: 120 },
                { text: this.$t('headers.buyer'), value:'to', width: 200 },
                { text: this.$t('headers.ref-contract-no'), value:'referenceContract', width: 110 },
                { text: this.$t('headers.ref-invoice-no'), value:'referenceInvoice', width: 110 },
                { text: this.$t('headers.packing-date'), value:'packingDate', class:'fixed-width-date', cellClass:'fixed-width-date' },
                // { text: this.$t('headers.expiry-date'), value:'expiryDate', cellClass:'fixed-width-date' },
                { text: this.$t('headers.packing'), value:'packing' },
                { text: this.$t('headers.actions'), value:'actions', width: 150 },
            ]

            // set shared props
            for (let i = 0; i < headers.length; i++) {
                headers[i].align = 'center';
                headers[i].class = headers[i].class ? `${headers[i].class} px-2` : 'px-2';
                headers[i].cellClass = headers[i].cellClass ? `${headers[i].cellClass} px-2` : 'px-2';
            }

            return headers;
        },
    },

    mounted(){
        this.loading = true;
        Promise.all([
            this.$store.dispatch('customers/fetchAll'),
            this.$store.dispatch('packingLists/fetchAll', { sequence: null, to: null, date: null })
        ])
        .finally(() => {
            this.loading = false;
            this.filterLoading = false;
        })
    }
}
</script>

<style>

</style>